import axios from 'axios';
import jwtDecode from 'jwt-decode';

const endPoint = process.env.REACT_APP_API_ENDPOINT;

const willExpire = (token: string, inMinute = 10) => {
	const userData: any = jwtDecode(token);

	if (!userData.exp) {
		return false;
	}
	const exp = userData.exp * 1000;
	const date = new Date();
	date.setMinutes(date.getMinutes() + inMinute);
	return date.getTime() > exp;
};

const checkTokenExpire = async () => {
	let accessExpire = false;
	try {
		accessExpire = willExpire(localStorage.accessToken, 5 * 60);
	} catch {
		// Token is missing, malformed, or expired — treat as expired and redirect
		localStorage.clear();
		sessionStorage.clear();
		window.location.href = '/login';
		return { accessToken: null, refreshToken: null };
	}

	let accessToken = localStorage.accessToken;
	let refreshToken = localStorage.refreshToken;

	if (accessExpire) {
		try {
			const response = await axios.post(`${endPoint}/auth/refresh-token`, {
				accessToken: localStorage.accessToken,
				refreshToken: localStorage.refreshToken,
			});

			if (response.data.data) {
				accessToken = response.data.data.accessToken;
				refreshToken = response.data.data.refreshToken;
				localStorage.setItem('accessToken', accessToken);
				localStorage.setItem('refreshToken', refreshToken);
			}

			return { accessToken, refreshToken };
		} catch (err) {
			localStorage.clear();
			sessionStorage.clear();
			window.location.href = '/login';
		}
	}

	return { accessToken, refreshToken };
};

export const apiConfig = async (flag = false) => {
	if (localStorage.getItem('accessToken')) {
		const tokens = await checkTokenExpire();

		return {
			headers: {
				Authorization: `bearer ${tokens.accessToken}`,
				RefreshToken: `bearer ${tokens.refreshToken}`,
				'Content-Type': flag ? 'multipart/form-data' : 'application/json',
			},
			method: 'PUT,DELETE,POST,GET,OPTION',
		};
	}
	return {};
};

export const getApi = async (
	url?: string,
	params?: any,
	signal?: AbortSignal
) => {
	const configData = await apiConfig();
	return axios.get(`${endPoint}${url}`, {
		params: params,
		signal,
		...configData,
	});
};

export const getApiPDF = async (url?: string, params?: any) => {
	const configData: any = await apiConfig();
	configData.headers['responseType'] = 'blob';
	return axios.get(`${endPoint}${url}`, {
		params: params,
		...configData,
	});
};

export const getApiCSV = async (url?: string, params?: any) => {
	const configData = await apiConfig();
	return axios.get(`${endPoint}${url}`, {
		params: params,
		responseType: 'arraybuffer',
		...configData,
	});
};

export const getApiZip = async (url?: string, params?: any) => {
	const configData = await apiConfig();

	return axios.post(`${endPoint}${url}`, params, {
		responseType: 'arraybuffer',
		...configData,
	});
};

export const postApi = async (
	url: string,
	apiData?: any,
	flag?: boolean,
	params?: any,
	signal?: AbortSignal
) => {
	const configData = await apiConfig(flag);
	return axios.post(`${endPoint}${url}`, apiData, {
		params,
		signal,
		...configData,
	});
};

export const postWithParams = async (
	url: string,
	apiData?: any,
	flag?: boolean
) => {
	const configData = await apiConfig(flag);
	return axios.post(`${endPoint}${url}`, apiData, configData);
};

export const putApi = async (url: string, apiData: any, flag?: boolean) => {
	const configData = await apiConfig(flag);
	return axios.put(`${endPoint}${url}`, apiData, configData);
};

export const patchApi = async (url: string, apiData?: any, flag?: boolean) => {
	const configData = await apiConfig(flag);
	return axios.patch(`${endPoint}${url}`, apiData, configData);
};

export const putApiParams = async (url: string, apiData: any, params?: any) => {
	const configData = await apiConfig(false);
	return axios.put(`${endPoint}${url}`, apiData, { ...configData, params });
};

export const putApiNoHeader = async (url: string, apiData: any) => {
	return axios.put(`${endPoint}${url}`, apiData, {
		headers: {
			Authorization: `bearer ${localStorage.accessToken}`,
			RefreshToken: `bearer ${localStorage.refreshToken}`,
		},
	});
};

// For FormData payloads — omits Content-Type so axios/the browser can set
// the multipart boundary itself, which a forced 'multipart/form-data' header
// (see apiConfig above) would break.
export const postApiNoHeader = async (
	url: string,
	apiData: any,
	signal?: AbortSignal
) => {
	return axios.post(`${endPoint}${url}`, apiData, {
		signal,
		headers: {
			Authorization: `bearer ${localStorage.accessToken}`,
			RefreshToken: `bearer ${localStorage.refreshToken}`,
		},
	});
};

// POSTs multipart form data and consumes a Server-Sent Events response,
// invoking onEvent for each parsed `data:` payload. Used by the chat to stream
// the assistant's reply as it is generated (see ChatBubble). The browser sets
// the multipart boundary itself, so Content-Type is deliberately not forced.
// Throws before any event if the response isn't a 2xx event-stream, so the
// caller can fall back to the non-streaming /ai/chat endpoint; the thrown error
// carries `.response` (status + parsed JSON body) like an axios error would.
export const postApiStream = async (
	url: string,
	formData: FormData,
	onEvent: (event: any) => void,
	signal?: AbortSignal
): Promise<void> => {
	const configData: any = await apiConfig();
	const headers: Record<string, string> = {};
	if (configData?.headers?.Authorization) headers.Authorization = configData.headers.Authorization;
	if (configData?.headers?.RefreshToken) headers.RefreshToken = configData.headers.RefreshToken;

	const res = await fetch(`${endPoint}${url}`, {
		method: 'POST',
		body: formData,
		headers,
		signal,
	});

	const contentType = res.headers.get('content-type') || '';
	if (!res.ok || !res.body || !contentType.includes('text/event-stream')) {
		const err: any = new Error(`Chat stream unavailable (status ${res.status})`);
		err.status = res.status;
		// Surface a JSON error body (e.g. a validation 400) the same shape axios
		// would, so the caller's existing error handling keeps working.
		try {
			err.response = { status: res.status, data: await res.json() };
		} catch {
			err.response = { status: res.status, data: undefined };
		}
		// A missing event-stream content-type with a 2xx is just "no streaming
		// here" — mark it so the caller falls back silently rather than erroring.
		if (res.ok) err.fallback = true;
		throw err;
	}

	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		// SSE frames are separated by a blank line.
		let sep: number;
		while ((sep = buffer.indexOf('\n\n')) !== -1) {
			const frame = buffer.slice(0, sep);
			buffer = buffer.slice(sep + 2);
			const data = frame
				.split('\n')
				.filter((line) => line.startsWith('data:'))
				.map((line) => line.slice(5).replace(/^ /, ''))
				.join('\n');
			if (!data) continue; // keep-alive comment or empty frame
			try {
				onEvent(JSON.parse(data));
			} catch {
				/* ignore a malformed frame */
			}
		}
	}
};

export const deleteApi = async (url: string) => {
	const configData = await apiConfig();
	return axios.delete(`${endPoint}${url}`, configData);
};

export const deleteApiWithData = async (url: string, apiData: any) => {
	const configData = await apiConfig();
	return axios.delete(`${endPoint}${url}`, {
		data: apiData,
		...configData,
	});
};
